import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Calculator, Settings, FileText, BarChart3, Play, ClipboardList,
  AlertTriangle, TrendingUp, DollarSign, Percent, ArrowRight,
  Calendar, RefreshCw, CheckCircle
} from 'lucide-react';

export default function TributarioDashboard() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [ultimaApuracao, setUltimaApuracao] = useState(null);
  const [resumoAnual, setResumoAnual] = useState(null);
  const [canonical, setCanonical] = useState(null); // V5.2 · fonte canônica db.titles
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // V5.2 · shadow mode canônico (?engine=legacy desativa)
      const useLegacy = new URLSearchParams(window.location.search).get('engine') === 'legacy';
      const year = new Date().getFullYear();
      const [configRes, apuracoesRes, canonicalRes] = await Promise.all([
        api.get('/tributario/config'),
        api.get('/tributario/apuracoes', { params: { ano: year } }),
        useLegacy
          ? Promise.resolve({ data: null })
          : api.get('/finance/tax-summary', { params: { year } }).catch(() => ({ data: null })),
      ]);

      setConfig(configRes.data);

      if (apuracoesRes.data.apuracoes?.length > 0) {
        setUltimaApuracao(apuracoesRes.data.apuracoes[0]);
      }

      if (canonicalRes.data) {
        setCanonical(canonicalRes.data);
        console.info('[tributario] motor canônico', canonicalRes.data);
      }

      // Buscar resumo anual
      try {
        const resumoRes = await api.get('/tributario/relatorios/resumo-anual', {
          params: { ano: year }
        });
        setResumoAnual(resumoRes.data);
      } catch (e) {
        console.log('Sem resumo anual ainda');
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApurarMesAtual = async () => {
    const hoje = new Date();
    try {
      const response = await api.post('/tributario/apurar', {
        competencia_mes: hoje.getMonth() + 1,
        competencia_ano: hoje.getFullYear(),
        tipo: 'mensal',
        forcar_recalculo: false
      });
      toast.success('Apuração realizada com sucesso!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao apurar tributos');
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-[300px]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tributario-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="w-7 h-7 text-purple-600" />
            Fiscal e Tributos
          </h1>
          <p className="text-muted-foreground">Apuração tributária - Lucro Presumido</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/tributario/config')}>
            <Settings className="w-4 h-4 mr-2" />
            Configurar
          </Button>
          <Button onClick={handleApurarMesAtual}>
            <Play className="w-4 h-4 mr-2" />
            Apurar Mês Atual
          </Button>
        </div>
      </div>

      {/* Status da Configuração */}
      {!config?.configurado && (
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader>
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Configuração Necessária
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-amber-700 mb-4">
              Configure os parâmetros tributários antes de iniciar as apurações.
            </p>
            <Button onClick={() => navigate('/tributario/config')}>
              Configurar Parâmetros
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Cards de Resumo do Ano (canônico substitui receita quando disponível) */}
      {(resumoAnual || canonical) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Receita Tributável</CardTitle>
              <DollarSign className="w-5 h-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="tributario-receita">
                R$ {(canonical?.receita_tributavel ?? resumoAnual?.totais?.receita_faturada ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">
                {canonical ? <span className="text-purple-600">·canônico</span> : (resumoAnual?.ano || new Date().getFullYear())}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total de Tributos</CardTitle>
              <Calculator className="w-5 h-5 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                R$ {(resumoAnual.totais?.total_tributos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">{resumoAnual.meses_apurados} mês(es) apurado(s)</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Carga Média</CardTitle>
              <Percent className="w-5 h-5 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(resumoAnual.totais?.carga_media || 0).toFixed(2)}%
              </div>
              <p className="text-xs text-muted-foreground">Sobre faturamento</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Última Apuração</CardTitle>
              <Calendar className="w-5 h-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              {ultimaApuracao ? (
                <>
                  <div className="text-2xl font-bold">
                    {ultimaApuracao.competencia_mes?.toString().padStart(2, '0')}/{ultimaApuracao.competencia_ano}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    R$ {(ultimaApuracao.totais?.total_tributos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground">Nenhuma apuração</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Menu de Navegação */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/tributario/apuracao')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-green-600" />
              Apuração de Tributos
            </CardTitle>
            <CardDescription>
              Calcule tributos mensais ou trimestrais com memória de cálculo completa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              Acessar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/tributario/config')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-600" />
              Configurações Tributárias
            </CardTitle>
            <CardDescription>
              Parâmetros, alíquotas e vigências editáveis
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1 text-sm text-muted-foreground mb-4">
              {config?.configurado ? (
                <>
                  <p>Regime: <strong>{config.regime}</strong></p>
                  <p>ISS: <strong>{(config.aliquota_iss * 100).toFixed(2)}%</strong></p>
                  <p>Presunção IRPJ: <strong>{(config.presuncao_irpj * 100).toFixed(0)}%</strong></p>
                </>
              ) : (
                <p className="text-amber-600">Não configurado</p>
              )}
            </div>
            <Button variant="outline" className="w-full">
              Configurar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/tributario/memoria')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Memória de Cálculo
            </CardTitle>
            <CardDescription>
              Detalhamento auditável de cada apuração
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              Consultar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/tributario/relatorios')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Relatórios Fiscais
            </CardTitle>
            <CardDescription>
              Resumos mensais, trimestrais e comparativos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              Ver Relatórios <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/tributario/simulacoes')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-600" />
              Simulações
            </CardTitle>
            <CardDescription>
              Cenários e projeções tributárias (CBS/IBS 2026)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              Simular <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/tributario/logs')}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-gray-600" />
              Logs e Auditoria
            </CardTitle>
            <CardDescription>
              Histórico de alterações e recálculos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full">
              Ver Logs <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Tabela de Tributos por Mês */}
      {resumoAnual?.mensal?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tributos por Competência - {resumoAnual.ano}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Mês</th>
                    <th className="text-right py-2">Receita</th>
                    <th className="text-right py-2">PIS</th>
                    <th className="text-right py-2">COFINS</th>
                    <th className="text-right py-2">ISS</th>
                    <th className="text-right py-2">IRPJ</th>
                    <th className="text-right py-2">CSLL</th>
                    <th className="text-right py-2 font-bold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {resumoAnual.mensal.map((m) => (
                    <tr key={m.mes} className="border-b hover:bg-muted/50">
                      <td className="py-2 font-medium">
                        {['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'][m.mes]}
                      </td>
                      <td className="text-right py-2">
                        R$ {(m.receita_faturada || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right py-2">
                        R$ {(m.pis || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right py-2">
                        R$ {(m.cofins || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right py-2">
                        R$ {(m.iss || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right py-2">
                        R$ {(m.irpj || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right py-2">
                        R$ {(m.csll || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right py-2 font-bold text-purple-600">
                        R$ {(m.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
